import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, MapPin } from 'lucide-react';
import { api } from '../lib/api';
import type { PublicVenue } from '../lib/types';
import { useTitle } from '../ui';

/**
 * The front door of an install: venues taking bookings, plus the way into the
 * portal for staff. Deliberately plain - each venue's own page is the star.
 */
export function Directory({ notFound }: { notFound?: boolean }) {
  useTitle('SpaceBooking');
  const q = useQuery({ queryKey: ['directory'], queryFn: () => api<{ venues: PublicVenue[] }>('/api/public/venues') });
  return (
    <div className="dir">
      <header className="dir-head">
        <div className="dir-brand">
          <img src="/favicon.svg" alt="" width={28} height={28} />
          <span>SpaceBooking</span>
        </div>
        <Link className="btn btn-sm" to="/admin">
          Venue sign in
        </Link>
      </header>
      <main className="dir-main">
        {notFound && <p className="dir-404">That page doesn't exist — here's where you can book instead.</p>}
        <h1 className="display dir-title">Book a table</h1>
        <p className="dir-sub">Choose a venue, pick a time and see exactly where you'll sit.</p>
        <div className="dir-list">
          {q.isPending &&
            Array.from({ length: 2 }, (_, i) => <div key={i} className="skeleton" style={{ height: 112, borderRadius: 12 }} />)}
          {q.data?.venues.map((v) => (
            <Link key={v.id} to={`/book/${v.slug}`} className="dir-card" style={{ ['--brand' as string]: v.brand_color }}>
              <span className="dir-swatch" />
              <span className="grow">
                <span className="dir-name display">{v.name}</span>
                <span className="dir-desc">{v.description}</span>
                {v.address && (
                  <span className="dir-addr">
                    <MapPin /> {v.address}
                  </span>
                )}
              </span>
              <ArrowRight className="dir-go" />
            </Link>
          ))}
          {q.data && q.data.venues.length === 0 && <p className="muted">No venues are taking bookings yet.</p>}
        </div>
      </main>
    </div>
  );
}
